import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { projectsApi, tasksApi, documentsApi } from '../../services/api';
import {
  useTasksQuery,
  useProjectsQuery,
  useProjectSectionsQuery,
  useDocumentsQuery,
  useCustomFieldsQuery,
  useProjectChatsQuery,
  useMyBotsQuery,
} from '../../hooks/queries';
import { taskKeys, projectKeys } from '../../lib/queryKeys';
import { useDateTime } from '../../contexts/DateTimeContext';
import { useTask } from '../../contexts/TaskContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import AddProjectModal from '../../components/AddProjectModal';
import ProjectUnreadDot from '../../components/unread/ProjectUnreadDot';
import AddProjectToTeamModal from '../../components/AddProjectToTeamModal';
import ShareProjectModal from '../../components/ShareProjectModal';
import ActivityLogModal from '../../components/ActivityLogModal';
import ProjectChatPanel from '../../components/bots/ProjectChatPanel';
import { ChatsHistory } from './ChatsPage';
import BotAvatar, { botState } from '../../components/bots/BotAvatar';
import { useProjectEligibleBots } from '../../components/bots/ComposerBotPicker';
import CreateMenu from '../../components/create/CreateMenu';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../../components/ui/sheet';
import { useIsMobile } from '../../hooks/useIsMobile';
import ProjectContextMenu from '../../components/ProjectContextMenu';
import KanbanBoard from '../../components/KanbanBoard';
import ProjectCalendar from '../../components/ProjectCalendar';
import ViewMenu from '../../components/ViewMenu';
import ListView from '../../components/ListView';
import TimelineView from '../../components/TimelineView';
import GalleryView from '../../components/GalleryView';
import { PageHeaderSkeleton, TaskListSkeleton } from '../../components/skeletons/PageSkeletons';
import ChangelogView from '../../components/ChangelogView';  // renders changelog DOCUMENTS feed when project.layout === 'changelog'
import DocumentsBrowser from '../../components/DocumentsBrowser';
import { isDocumentLayout, isPrimaryDocLayout, isChangelogLayout } from '../../lib/projectLayouts';
import BulkSelectableTable from '../../components/BulkSelectableTable';
import ProjectListBlock, { useCollapsedLists } from '../../components/projects/ProjectListBlock';
import ProjectTabs from '../../components/projects/ProjectTabs';
import TaskFilterBar from '../../components/tasks/TaskFilterBar';
import { useViewTabs } from '../../hooks/useViewTabs';
import { usePageTaskFilters } from '../../hooks/usePageTaskFilters';
import {
  Plus, Search, ChevronDown, ChevronRight,
  Folder, MoreHorizontal, Star, ListTodo,
  LayoutGrid, List as ListIcon,
} from 'lucide-react';
import { useSettingsQuery } from '../../hooks/queries';
import { useUpdateSettings } from '../../hooks/mutations';
import { APP_COLORS as PROJECT_COLORS } from '../../constants/colors';
import { useMobileTitle } from '../../components/mobile/mobileTitle';
import { lastProject } from '../../components/bots/lastProject';
import { EMPTY_ARRAY } from '../../lib/empty';
import { cn } from '../../lib/utils';

// The project's chat panel on wide screens (see startPanelResize).
const PANEL_WIDTH_KEY = 'bb.projectChat.width';
const PANEL_MIN = 360;
const PANEL_MAX = 1000;
const PANEL_DEFAULT = 'clamp(400px, 34vw, 640px)';

const ProjectsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { formatDate } = useDateTime();
  const { openTaskCreation } = useTask();
  const qc = useQueryClient();

  // Selected project for detail view
  const selectedProjectId = searchParams.get('project');
  useEffect(() => { if (selectedProjectId) lastProject.set(selectedProjectId); }, [selectedProjectId]);

  const tasksQuery = useTasksQuery();
  // An open project loads its own tasks: the list of all tasks keeps only the latest
  // done ones for people who see many (backend GET /tasks), so a project's older done
  // tasks would be missing. Until they arrive, the list of all tasks stands in.
  const projectTasksQuery = useTasksQuery(
    { project_id: selectedProjectId },
    { enabled: !!selectedProjectId }
  );
  const projectsQuery = useProjectsQuery();
  const tasks = (selectedProjectId && projectTasksQuery.data) || tasksQuery.data || [];
  const projects = projectsQuery.data || EMPTY_ARRAY;
  const tasksRef = useRef([]);
  tasksRef.current = tasks;
  const loading = tasksQuery.isPending || projectsQuery.isPending;
  const selectedProject = projects.find(p => p.id === selectedProjectId);
  useMobileTitle(selectedProject?.name);
  const sectionParam = searchParams.get('section');
  const panelMode = searchParams.get('panel');  // 'chat' | 'chats' | null
  const panelBot = searchParams.get('pbot');
  const panelChat = searchParams.get('pchat');
  const setPanel = useCallback((change) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (change === null) { ['panel', 'pbot', 'pchat'].forEach((k) => next.delete(k)); return next; }
      if (change.mode !== undefined) next.set('panel', change.mode);
      if (change.bot !== undefined) { if (change.bot) next.set('pbot', change.bot); else next.delete('pbot'); }
      if (change.chat !== undefined) { if (change.chat) next.set('pchat', change.chat); else next.delete('pchat'); }
      return next;
    });
  }, [setSearchParams]);
  const [createOpen, setCreateOpen] = useState(false);
  const projectBots = useProjectEligibleBots(selectedProject);
  const isMobile = useIsMobile();
  const [wideScreen, setWideScreen] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches);
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1024px)');
    const onChange = (e) => setWideScreen(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  // The chat next to the project: a third of the screen by default (400-640px), or
  // as wide as the person dragged it (remembered); a double click on the edge resets it.
  const [panelWidth, setPanelWidth] = useState(() => {
    try {
      const saved = Number(localStorage.getItem(PANEL_WIDTH_KEY));
      return saved >= PANEL_MIN ? saved : null;
    } catch { return null; }
  });
  const panelWidthRef = useRef(panelWidth);
  panelWidthRef.current = panelWidth;
  const startPanelResize = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = e.currentTarget.parentElement.getBoundingClientRect().width;
    const max = Math.max(PANEL_MIN, Math.min(PANEL_MAX, window.innerWidth - 760));
    const move = (ev) => setPanelWidth(Math.round(Math.min(max, Math.max(PANEL_MIN, startWidth + startX - ev.clientX))));
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      try { if (panelWidthRef.current) localStorage.setItem(PANEL_WIDTH_KEY, String(panelWidthRef.current)); } catch { /* private mode */ }
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }, []);
  const resetPanelWidth = useCallback(() => {
    setPanelWidth(null);
    try { localStorage.removeItem(PANEL_WIDTH_KEY); } catch { /* private mode */ }
  }, []);

  // View tabs - per-section when drilled into a list, per-project otherwise.
  // Section-scoped views isolate each list's tab config (visible_fields, sort,
  // filters, active view) so switching layout on one list doesn't rewrite
  // sibling lists in the same project.
  const viewScope = useMemo(
    () => sectionParam
      ? { kind: 'section', sectionId: sectionParam }
      : { kind: 'project', projectId: selectedProjectId },
    [sectionParam, selectedProjectId],
  );
  const viewTabs = useViewTabs(viewScope);
  const { tabs, activeView, hiddenCatalog, isLocked, loading: viewsLoading, actions: viewActions } = viewTabs;
  const activeViewId = activeView?.id || null;
  const viewMode = activeView?.type || null;

  const projectSectionsQuery = useProjectSectionsQuery(selectedProjectId);
  const projectSections = projectSectionsQuery.data || EMPTY_ARRAY;
  const projectDocsQuery = useDocumentsQuery(
    selectedProjectId ? { project_id: selectedProjectId } : {},
    { enabled: !!selectedProjectId }
  );
  const projectDocs = projectDocsQuery.data || [];
  const customFieldsQuery = useCustomFieldsQuery(selectedProjectId);
  const customFieldDefs = customFieldsQuery.data || [];
  const [activeSection, setActiveSection] = useState(null);
  const [renamingSectionId, setRenamingSectionId] = useState(null);
  const [renamingLocation, setRenamingLocation] = useState(null); // 'title' (the opened list) | 'section' (a list on the Tasks tab)
  const [renameSectionValue, setRenameSectionValue] = useState('');
  const [isRenamingProject, setIsRenamingProject] = useState(false);
  const [renameProjectValue, setRenameProjectValue] = useState('');

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [showAddToTeamModal, setShowAddToTeamModal] = useState(false);
  const [addingToTeamProject, setAddingToTeamProject] = useState(null);
  const [shareModalProject, setShareModalProject] = useState(null);
  const [activityLogProject, setActivityLogProject] = useState(null);

  // Context menu
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, project: null });

  // Documents list/grid mode — persisted per user via ui_preferences so the
  // user's preferred layout sticks across devices and projects.
  const settingsQuery = useSettingsQuery();
  const updateSettings = useUpdateSettings();
  const documentsViewMode = settingsQuery.data?.ui_preferences?.documents_view_mode || 'grid';
  const setDocumentsViewMode = (mode) => {
    updateSettings.mutate({ 'ui_preferences.documents_view_mode': mode });
  };

  // Filters for project list
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedProjectIds, setExpandedProjectIds] = useState(new Set());

  // Listen for project deletion from global DeleteProjectModal
  useEffect(() => {
    const handleProjectDeleted = (e) => {
      // Cache invalidation happens via the axios interceptor; just navigate.
      if (selectedProjectId === e.detail.projectId) {
        setSearchParams({});
      }
    };
    window.addEventListener('projectDeleted', handleProjectDeleted);
    return () => window.removeEventListener('projectDeleted', handleProjectDeleted);
  }, [selectedProjectId, setSearchParams]);

  // Handle ?action=edit URL param (dispatched from sidebar context menu)
  const actionParam = searchParams.get('action');
  useEffect(() => {
    if (actionParam === 'edit' && selectedProject) {
      handleEditProject(selectedProject);
      setSearchParams({ project: selectedProjectId }, { replace: true });
    }
  }, [actionParam, selectedProject, selectedProjectId, setSearchParams]);

  useEffect(() => {
    if (selectedProjectId) {
      // Sections/docs are loaded by useProjectSectionsQuery and useDocumentsQuery above.
      projectsApi.access(selectedProjectId).catch(() => {});
      setActiveSection(sectionParam || null);
    } else {
      setActiveSection(null);
    }
  }, [selectedProjectId, sectionParam]);

  // If the active section gets deleted (e.g. from the sidebar context menu),
  // the detail view would otherwise stay pinned to a section id that no longer
  // exists - showing a stale header and an empty task list. Once sections have
  // loaded, fall back to the project overview when the active section is gone.
  useEffect(() => {
    if (!selectedProjectId || !activeSection || activeSection === 'all') return;
    if (!projectSectionsQuery.isSuccess) return;
    if (projectSections.some(s => s.id === activeSection)) return;
    setActiveSection(null);
    setSearchParams({ project: selectedProjectId }, { replace: true });
  }, [activeSection, projectSections, projectSectionsQuery.isSuccess, selectedProjectId, setSearchParams]);

  // Primary-doc layouts (whiteboard / document / mindmap / embed / database)
  // are anchored on a single document. When the user lands on such a project
  // we look up the first document of that type and forward them straight to
  // its editor - same UX as opening the document from the sidebar.
  // Changelog is excluded (it's a feed, no primary doc).
  // The redirect uses { replace: true } so the back button doesn't ping-pong.
  const selectedLayout = selectedProject?.layout;
  useEffect(() => {
    if (!selectedProjectId || !selectedLayout) return;
    if (!isPrimaryDocLayout(selectedLayout)) return;
    // Cancel flag guards against the rapid-click race: clicking through
    // several primary-doc projects fires one getAll per click, and without
    // this guard a stale resolution would `navigate()` the user away from
    // whatever project they ultimately landed on. Capturing the project id
    // at request time and verifying it on resolve makes the redirect a no-op
    // when the user has already moved on.
    let cancelled = false;
    const requestedProjectId = selectedProjectId;
    documentsApi
      .getAll({ project_id: requestedProjectId, type: selectedLayout })
      .then(r => {
        if (cancelled) return;
        const docs = r.data || [];
        if (docs.length > 0) {
          navigate(`/app/documents/${docs[0].id}`, { replace: true });
        }
        // Otherwise we stay on the project page and render an empty state CTA
        // letting the user create the missing primary document.
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedProjectId, selectedLayout, navigate]);

  const handleCreatePrimaryDoc = useCallback(async () => {
    if (!selectedProject || !isPrimaryDocLayout(selectedProject.layout)) return;
    try {
      const res = await documentsApi.create({
        type: selectedProject.layout,
        title: selectedProject.name,
        project_id: selectedProject.id,
        team_id: selectedProject.team_id || null,
      });
      const newId = res?.data?.id;
      if (newId) navigate(`/app/documents/${newId}`);
    } catch {
      // 402 surfaces an upgrade prompt via the api interceptor.
    }
  }, [selectedProject, navigate]);

  // Custom field defs flow through useCustomFieldsQuery; the legacy
  // customFieldsUpdated event still fires from CustomFields settings code,
  // so map it to a query invalidation here.
  useEffect(() => {
    const handler = () =>
      qc.invalidateQueries({ queryKey: ['custom-fields'] });
    window.addEventListener('customFieldsUpdated', handler);
    return () => window.removeEventListener('customFieldsUpdated', handler);
  }, [qc]);

  // Imperative refresh used by handlers and post-action flows. Replaces the
  // old fetchData() which set local state - now we just invalidate caches.
  const fetchData = useCallback(() => {
    qc.invalidateQueries({ queryKey: taskKeys.all });
    qc.invalidateQueries({ queryKey: projectKeys.all });
  }, [qc]);

  // Filter projects
  const filteredProjects = useMemo(() => {
    const matched = projects.filter(project => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!project.name.toLowerCase().includes(query) &&
            !(project.description || '').toLowerCase().includes(query)) {
          return false;
        }
      }
      return true;
    });
    // Favorites first, then alphabetical by name (locale-aware so accented
    // letters in non-English locales sort the way users expect).
    return [...matched].sort((a, b) => {
      const favA = a.is_favorite ? 0 : 1;
      const favB = b.is_favorite ? 0 : 1;
      if (favA !== favB) return favA - favB;
      return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
    });
  }, [projects, searchQuery]);

  // Project views show every task in the project; narrowing to "my tasks"
  // happens explicitly via the table filter presets, not a hidden global toggle.
  const visibleTasks = tasks;

  // Get tasks for selected project (optionally filtered by section)
  const projectTasks = useMemo(() => {
    if (!selectedProjectId) return [];
    let filtered = visibleTasks.filter(t => t.project_id === selectedProjectId);
    if (activeSection && activeSection !== 'all') {
      filtered = filtered.filter(t => t.section_id === activeSection);
    }
    return filtered;
  }, [visibleTasks, selectedProjectId, activeSection]);

  // The tasks one list of the open project shows (null: the main list). A task whose list
  // was deleted keeps its section_id (so a restore brings it back) and shows in the main
  // list until then. Subtasks are tasks like any other: the table nests them under their
  // parent (lib/subtasks.js), so no list leaves them out.
  const liveSectionIds = useMemo(() => new Set(projectSections.map(s => s.id)), [projectSections]);
  const tasksOfList = useCallback((sectionId) => visibleTasks.filter(t =>
    t.project_id === selectedProjectId &&
    (sectionId ? t.section_id === sectionId : (!t.section_id || !liveSectionIds.has(t.section_id)))
  ), [visibleTasks, selectedProjectId, liveSectionIds]);

  // The Tasks tab: one search and filter for every list and view of the project (or of
  // the one list opened). Saved where the main list's table kept its own filters.
  const filters = usePageTaskFilters({
    tasks: projectTasks,
    viewId: activeViewId ? (activeSection && activeSection !== 'all'
      ? `view-${activeViewId}-section-${activeSection}`
      : `view-${activeViewId}-main`) : null,
    projects,
    teamId: selectedProject?.team_id || null,
    projectId: selectedProjectId,
  });
  const shownTasks = filters.filteredTasks;
  // Another project or list starts unfiltered (or with what was saved for it); the
  // filters only load when something was saved, so the last ones would stay.
  const { clearFilters } = filters;
  useEffect(() => { clearFilters(); }, [selectedProjectId, activeSection, clearFilters]);
  const shownOfList = useCallback((sectionId) => shownTasks.filter(t =>
    sectionId ? t.section_id === sectionId : (!t.section_id || !liveSectionIds.has(t.section_id))
  ), [shownTasks, liveSectionIds]);
  // A table with no list header puts its controls into the toolbar (as on Tasks). Only a
  // new slot counts: a kept tab that is hidden lets its refs go (layouts/TabScreens.jsx).
  const [toolbarSlot, setToolbarSlot] = useState(null);
  const keepToolbarSlot = useCallback((el) => { if (el) setToolbarSlot(el); }, []);
  const [collapsedLists, toggleList] = useCollapsedLists(selectedProjectId);

  // Tasks · Docs · Chats (?tab=), Tasks unless the address names another.
  const tabParam = searchParams.get('tab');
  const projectTab = tabParam === 'docs' || tabParam === 'chats' ? tabParam : 'tasks';
  const setProjectTab = useCallback((key) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (key === 'tasks') next.delete('tab'); else next.set('tab', key);
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const projectChatsQuery = useProjectChatsQuery(selectedProjectId);
  const [chatFilters, setChatFilters] = useState({});
  const { data: myBots } = useMyBotsQuery({ enabled: !!selectedProjectId });
  // The chats ChatHistory lists: those with the person's bots.
  const chatsCount = useMemo(() => {
    const mine = new Set((myBots?.bots || []).map((b) => b.id));
    return (projectChatsQuery.data?.chats || []).filter((c) => mine.has(c.bot_id)).length;
  }, [projectChatsQuery.data, myBots]);

  // Task stats for each project
  const getProjectStats = (projectId) => {
    const projectTasks = visibleTasks.filter(t => t.project_id === projectId);
    const completed = projectTasks.filter(t => t.status === 'completed').length;
    const total = projectTasks.length;
    return { completed, total, pending: total - completed };
  };

  const handleProjectClick = (project) => {
    setSearchParams({ project: project.id });
  };

  const handleAddProject = () => {
    setEditingProject(null);
    setShowAddModal(true);
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setShowAddModal(true);
  };

  const handleRenameProject = async (project, newName) => {
    setIsRenamingProject(false);
    const trimmed = newName.trim();
    if (!trimmed || trimmed === project.name) return;
    try {
      await projectsApi.update(project.id, { name: trimmed });
      fetchData();
    } catch (err) {
      console.error('Error renaming project:', err);
    }
  };

  const handleDeleteProject = (project) => {
    // Dispatch global event to show delete modal in AppLayout
    window.dispatchEvent(new CustomEvent('deleteProjectRequest', { detail: { project } }));
  };

  const handleDuplicateProject = async (project) => {
    try {
      // BG_91: server-side deep copy - duplicates the project together with
      // its tasks, views, sections and documents (the old client-side call
      // only re-created the shell, landing as an empty project).
      await projectsApi.duplicate(project.id);
      toast.success(t('projects.project_duplicated', { name: project.name }));
      // The axios interceptor invalidates ['projects']/['tasks']/['documents']
      // for any /projects mutation, so the sidebar and this page both refetch
      // automatically - no extra event wiring needed.
      fetchData();
    } catch (err) {
      // A 402 means the project quota is hit - the api interceptor already
      // opens the upgrade modal, so skip the generic failure toast.
      if (err?.response?.status !== 402) {
        toast.error(t('projects.project_duplicate_failed'));
      }
      console.error('Error duplicating project:', err);
    }
  };

  const moveProject = async (project, direction) => {
    const section = projects
      .filter(p => (project.team_id ? p.team_id === project.team_id : !p.team_id) && !!p.is_favorite === !!project.is_favorite)
      .sort((a, b) => a.order !== b.order ? a.order - b.order : a.name.localeCompare(b.name));
    const idx = section.findIndex(p => p.id === project.id);
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= section.length) return;
    const reordered = [...section];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    const updates = reordered.map((p, i) => ({ id: p.id, order: i }));
    // Optimistic cache update so the reorder shows instantly.
    const orderMap = Object.fromEntries(updates.map(u => [u.id, u.order]));
    qc.setQueryData(projectKeys.list(), (prev = []) =>
      prev.map(p => orderMap[p.id] !== undefined ? { ...p, order: orderMap[p.id] } : p)
    );
    try {
      await projectsApi.reorder(updates);
    } catch {
      fetchData();
    }
  };

  const handleToggleFavorite = async (project) => {
    try {
      await projectsApi.update(project.id, { is_favorite: !project.is_favorite });
      fetchData();
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  };

  const handleAddToTeam = (project) => {
    console.log('handleAddToTeam called with project:', project);
    setAddingToTeamProject(project);
    setShowAddToTeamModal(true);
  };

  const handleAddToTeamSuccess = () => {
    fetchData();
    // Dispatch event to refresh sidebar
  };

  const handleProjectCreated = () => {
    fetchData();
    setShowAddModal(false);
    setEditingProject(null);
    // Dispatch event to refresh sidebar
  };

  const handleContextMenu = (e, project) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      show: true,
      x: e.clientX,
      y: e.clientY,
      project
    });
  };

  const closeContextMenu = () => {
    setContextMenu({ show: false, x: 0, y: 0, project: null });
  };

  const handleTaskUpdate = (newTask) => {
    if (newTask && !newTask.parent_id) {
      // Optimistically insert the new task into every cached task list.
      const lists = qc.getQueriesData({ queryKey: taskKeys.lists() });
      lists.forEach(([key, list]) => {
        if (!Array.isArray(list)) return;
        if (list.some(t => t.id === newTask.id)) return;
        qc.setQueryData(key, [newTask, ...list]);
      });
    } else {
      fetchData();
    }
  };

  const handleOptimisticUpdate = useCallback((taskId, updates) => {
    const lists = qc.getQueriesData({ queryKey: taskKeys.lists() });
    lists.forEach(([key, list]) => {
      if (!Array.isArray(list)) return;
      qc.setQueryData(
        key,
        list.map((t) => (t.id === taskId ? { ...t, ...updates } : t))
      );
    });
  }, [qc]);

  // ---- Cross-section drag-drop ----
  const [dragOverSectionId, setDragOverSectionId] = useState(null); // null = main list

  // Dropped on a list rather than on a task in it (TasksTable handles those): the task
  // goes to that list as a task of its own, out of its parent if it was a subtask.
  const handleMoveTaskToSection = useCallback(async (taskId, targetSectionId) => {
    const currentTask = tasksRef.current.find(t => t.id === taskId);
    if (!currentTask) return;
    const prev = { section_id: currentTask.section_id || null, parent_id: currentTask.parent_id || null };
    const next = { section_id: targetSectionId || null, parent_id: null };
    if (prev.section_id === next.section_id && !prev.parent_id) return;
    // Optimistic update against every cached task list.
    const setPlace = (val) => {
      const lists = qc.getQueriesData({ queryKey: taskKeys.lists() });
      lists.forEach(([key, list]) => {
        if (!Array.isArray(list)) return;
        qc.setQueryData(
          key,
          list.map((t) => (t.id === taskId ? { ...t, ...val } : t))
        );
      });
    };
    setPlace(next);
    try {
      await tasksApi.update(taskId, next);
    } catch (err) {
      console.error('Failed to move task to section:', err);
      setPlace(prev);
    }
  }, [qc]);

  // Over a task row the row decides (onto it or next to it, TasksTable); the list takes
  // only drops beside its rows.
  const makeSectionDropProps = useCallback((sectionId) => ({
    onDragOver: (e) => {
      if (e.target.closest?.('[data-task-row]')) { setDragOverSectionId(null); return; }
      e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverSectionId(sectionId ?? 'main');
    },
    onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverSectionId(null); },
    onDrop: (e) => {
      if (e.target.closest?.('[data-task-row]')) return;
      e.preventDefault();
      const taskId = e.dataTransfer.getData('text/plain');
      if (taskId) handleMoveTaskToSection(taskId, sectionId ?? null);
      setDragOverSectionId(null);
    },
  }), [handleMoveTaskToSection]);

  const handleRenameSection = useCallback(async (sectionId, newName) => {
    const trimmed = newName.trim();
    setRenamingSectionId(null);
    setRenamingLocation(null);
    if (!trimmed) return;
    try {
      await projectsApi.updateSection(sectionId, { name: trimmed });
      // Optimistic patch on the cached section list; interceptor invalidates anyway.
      qc.setQueryData(projectKeys.sections(selectedProjectId), (prev = []) =>
        prev.map(s => s.id === sectionId ? { ...s, name: trimmed } : s)
      );
      // Sidebar keeps its own (non-RQ) section state, so tell it to update too —
      // otherwise the rename only shows there after a full page refresh.
      window.dispatchEvent(new CustomEvent('sectionRenamed', {
        detail: { sectionId, projectId: selectedProjectId, name: trimmed },
      }));
    } catch (err) {
      console.error('Failed to rename section:', err);
    }
  }, [selectedProjectId, qc]);

  // Reflect section renames coming from the sidebar (which patches its own
  // state, not this view's React Query cache).
  useEffect(() => {
    const handler = (e) => {
      const { sectionId, projectId, name } = e.detail || {};
      if (!sectionId || !projectId) return;
      qc.setQueryData(projectKeys.sections(projectId), (prev) =>
        Array.isArray(prev) ? prev.map(s => s.id === sectionId ? { ...s, name } : s) : prev
      );
    };
    window.addEventListener('sectionRenamed', handler);
    return () => window.removeEventListener('sectionRenamed', handler);
  }, [qc]);

  if (loading) {
    return (
      <div className="space-y-4">
        <PageHeaderSkeleton />
        <TaskListSkeleton rows={6} />
      </div>
    );
  }

  // Create section handler
  // A new list starts with its name being typed (the default selected), so nobody has to
  // open it and rename it afterwards. Inside one list, the new one opens with its title;
  // otherwise the Tasks tab shows it.
  const handleCreateSection = async () => {
    try {
      const res = await projectsApi.createSection(selectedProjectId, { name: t('ui_pages.projects.new_list') });
      qc.setQueryData(projectKeys.sections(selectedProjectId), (prev = []) =>
        prev.some(s => s.id === res.data.id) ? prev : [...prev, res.data]
      );
      if (activeSection && activeSection !== 'all') {
        setActiveSection(res.data.id);
        setSearchParams({ project: selectedProjectId, section: res.data.id });
      } else {
        setProjectTab('tasks');
      }
      setRenameSectionValue(res.data.name);
      setRenamingSectionId(res.data.id);
      setRenamingLocation(activeSection && activeSection !== 'all' ? 'title' : 'section');
    } catch (err) { console.error(err); }
  };

  const handleCreateDoc = async (type = 'document') => {
    try {
      const res = await documentsApi.create({ title: '', type, content: type === 'document' ? '' : null, project_id: selectedProjectId });
      navigate(`/app/documents/${res.data.id}`, { state: { nameIt: true } });
    } catch (err) { console.error(err); }
  };

  // If a project is selected, show project detail view
  if (selectedProject) {
    const panel = selectedProject && panelMode ? (
      <ProjectChatPanel project={selectedProject} mode={panelMode === 'chats' ? 'chats' : 'chat'}
        botId={panelBot} chatId={panelChat} onChange={setPanel} onClose={() => setPanel(null)} className="h-full" />
    ) : null;
    const rootDocs = projectDocs.filter((d) => !d.parent_id);
    // One list opened (?section=, from the sidebar or a list's "..."): its own views.
    const inList = !!activeSection && activeSection !== 'all';
    const listSection = inList ? projectSections.find(s => s.id === activeSection) : null;
    const backToProject = () => { setActiveSection(null); setSearchParams({ project: selectedProjectId }); };
    const tab = inList ? 'tasks' : projectTab;
    const openCount = visibleTasks.filter(t => t.project_id === selectedProjectId && t.status !== 'completed').length;
    // Document projects show their document (or feed) instead of task views.
    const taskViews = inList || !isDocumentLayout(selectedProject.layout);
    // Table and List show the project's lists one under another; the other views show
    // the whole project at once.
    const sectioned = !inList && (viewMode === 'table' || viewMode === 'list');
    const listProps = {
      viewType: viewMode,
      visibleFields: activeView?.config?.visible_fields,
      customFieldDefs,
      projects,
      project: selectedProject,
      onTaskUpdate: handleTaskUpdate,
      onOptimisticUpdate: handleOptimisticUpdate,
    };
    const stopRenamingList = () => { setRenamingSectionId(null); setRenamingLocation(null); };
    const viewProps = {
      tasks: shownTasks,
      projects,
      onUpdate: handleTaskUpdate,
      prefillProjectId: selectedProjectId,
      prefillSectionId: inList ? activeSection : null,
      teamId: selectedProject.team_id || null,
      visibleFields: activeView?.config?.visible_fields,
      customFieldDefs,
    };

    const title = inList ? (
      renamingSectionId === activeSection && renamingLocation === 'title' ? (
        <input
          autoFocus
          onFocus={e => e.target.select()}
          className="text-xl font-semibold bg-transparent border-b border-primary outline-none text-foreground min-w-0 w-64 max-w-full"
          value={renameSectionValue}
          onChange={e => setRenameSectionValue(e.target.value)}
          onBlur={() => handleRenameSection(activeSection, renameSectionValue)}
          onKeyDown={e => {
            if (e.key === 'Enter') handleRenameSection(activeSection, renameSectionValue);
            if (e.key === 'Escape') stopRenamingList();
          }}
        />
      ) : (
        <h1
          className="text-xl font-semibold text-foreground truncate cursor-text select-none hover:border-b hover:border-dashed hover:border-muted-foreground"
          title={t('ui_pages.projects.click_to_rename')}
          onMouseDown={(e) => { e.preventDefault(); setRenamingSectionId(activeSection); setRenamingLocation('title'); setRenameSectionValue(listSection?.name || ''); }}
        >
          {listSection?.name || t('projects.table_tasks')}
        </h1>
      )
    ) : isRenamingProject ? (
      <input
        autoFocus
        className="text-xl font-semibold bg-transparent border-b border-primary outline-none text-foreground min-w-0 w-64 max-w-full"
        value={renameProjectValue}
        onChange={e => setRenameProjectValue(e.target.value)}
        onBlur={() => handleRenameProject(selectedProject, renameProjectValue)}
        onKeyDown={e => {
          if (e.key === 'Enter') handleRenameProject(selectedProject, renameProjectValue);
          if (e.key === 'Escape') setIsRenamingProject(false);
        }}
      />
    ) : (
      <h1
        className="max-md:hidden text-xl font-semibold text-foreground truncate cursor-text select-none hover:border-b hover:border-dashed hover:border-muted-foreground"
        title={t('ui_pages.projects.click_to_rename')}
        data-testid="project-title"
        onMouseDown={e => {
          e.preventDefault();
          setRenameProjectValue(selectedProject.name);
          setIsRenamingProject(true);
        }}
      >
        {selectedProject.name}
      </h1>
    );

    return (
      <div className="flex items-start gap-4">
      <div className="flex-1 min-w-0 space-y-4">
        {/* Header: where the project is (Projects · team), its color and name, and its
            actions: the bots (its chat), the one New, the "..." menu. On phones the top
            bar carries the name and the back arrow, and the tab bar's New is the New. */}
        <header className="space-y-1" data-testid="project-header">
          <nav aria-label={t('nav.projects')}
            className={cn(inList ? 'flex' : 'hidden md:flex', 'items-center gap-1.5 min-w-0 text-[12.5px] text-muted-foreground')}>
            <Link to="/app/projects" className="hover:text-foreground flex-shrink-0">{t('nav.projects')}</Link>
            {selectedProject.team_id && selectedProject.team_name && (
              <>
                <span aria-hidden className="text-muted-foreground/50">·</span>
                <Link to={`/app/tasks?team=${selectedProject.team_id}`} data-testid="project-team-crumb"
                  className="hover:text-foreground truncate">{selectedProject.team_name}</Link>
              </>
            )}
            {inList && (
              <>
                <span aria-hidden className="text-muted-foreground/50">·</span>
                <button type="button" onClick={backToProject} className="hover:text-foreground truncate">{selectedProject.name}</button>
              </>
            )}
          </nav>
          <div className="flex items-center gap-2.5 min-w-0 min-h-9">
            {inList
              ? <ListTodo className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              : <span className="w-3 h-3 rounded-[4px] flex-shrink-0" style={{ background: selectedProject.color || '#3B82F6' }} />}
            {title}
            {/* Phones carry the name in the top bar: this line says whose it is. */}
            {!inList && (
              <span className="md:hidden text-[13px] text-muted-foreground truncate">{selectedProject.team_name || t('projects.my_projects')}</span>
            )}
            {!inList && selectedProject.description && (
              <span className="hidden lg:block min-w-0 max-w-[48ch] truncate text-[13px] text-muted-foreground">{selectedProject.description}</span>
            )}
            <div className="flex items-center gap-1.5 flex-shrink-0 ml-auto">
              {projectBots.length > 0 && (
                <button type="button" onClick={() => (panelMode ? setPanel(null) : setPanel({ mode: 'chat' }))}
                  aria-pressed={!!panelMode} data-testid="project-ask-bot" aria-label={t('nav.create.chat')} title={t('nav.create.chat')}
                  className={`inline-flex items-center h-8 px-1 rounded-full transition-colors ${panelMode ? 'bg-accent' : 'hover:bg-accent'}`}>
                  <span className="flex -space-x-1.5">
                    {projectBots.slice(0, 3).map((b) => (
                      <BotAvatar key={b.id} bot={b} size="sm" state={botState(b)} />
                    ))}
                  </span>
                </button>
              )}
              {!isMobile && (
                <Popover open={createOpen} onOpenChange={setCreateOpen}>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="project-new"
                      className="inline-flex items-center gap-1.5 h-8 pl-2 pr-3 rounded-full bg-amber-500 text-black text-[13px] font-semibold hover:bg-amber-400 transition-colors">
                      <Plus className="w-4 h-4" strokeWidth={2.25} />{t('nav.create.new')}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-auto p-0 border-0 bg-transparent shadow-none">
                    <CreateMenu
                      project={selectedProject}
                      onTask={() => { setCreateOpen(false); openTaskCreation({ projectId: selectedProjectId, ...(inList ? { sectionId: activeSection } : {}) }); }}
                      onList={() => { setCreateOpen(false); handleCreateSection(); }}
                      onDocument={(type) => { setCreateOpen(false); handleCreateDoc(type); }}
                      onChat={() => { setCreateOpen(false); setPanel({ mode: 'chat', chat: null }); }}
                    />
                  </PopoverContent>
                </Popover>
              )}
              <button
                type="button"
                onClick={(e) => handleContextMenu(e, selectedProject)}
                aria-label={t('ui_pages.projects.project_menu')}
                title={t('ui_pages.projects.project_menu')}
                className="h-8 w-8 flex items-center justify-center hover:bg-accent rounded-md text-muted-foreground hover:text-foreground flex-shrink-0"
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>
            </div>
          </div>
        </header>

        {/* Tasks · Docs · Chats */}
        {!inList && (
          <ProjectTabs tab={tab} onTab={setProjectTab}
            counts={{ tasks: openCount, docs: rootDocs.length, chats: chatsCount }} />
        )}

        {tab === 'tasks' && (
          <div className="space-y-4">
            {/* Changelog projects render a chronological feed of changelog-type
                documents - no task views. An opened list shows its tasks instead. */}
            {isChangelogLayout(selectedProject.layout) && !inList && (
              <ChangelogView
                documents={projectDocs.filter(d => d.type === 'changelog')}
                projectId={selectedProjectId}
                teamId={selectedProject.team_id || null}
                onCreated={() => projectDocsQuery.refetch()}
              />
            )}

            {/* Primary-doc layouts (whiteboard / document / database / mindmap
                / embed) auto-redirect to their primary document via useEffect.
                This empty-state CTA is the recovery path: shown when the project
                has no document of its layout type yet (e.g. plan gate blocked the
                initial create, or the doc was deleted). */}
            {isPrimaryDocLayout(selectedProject.layout) && !inList &&
              !projectDocs.some(d => d.type === selectedProject.layout) && (
              <div className="text-center py-16 border border-dashed border-border rounded-lg">
                <p className="text-sm text-muted-foreground">
                  {t('projects.primary_doc_empty', {
                    type: t(`projects.layout_${selectedProject.layout}`, selectedProject.layout),
                    defaultValue: 'No content yet for this project.',
                  })}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={handleCreatePrimaryDoc}
                >
                  {t('projects.primary_doc_create_cta', { defaultValue: 'Create one' })}
                </Button>
              </div>
            )}

            {taskViews && (
              <>
                {/* One toolbar: search and filter for every list, and the project's view
                    (shared with the team; order and default are per person). */}
                <div className="flex items-start gap-1.5">
                  <div className="flex-1 min-w-0">
                    <TaskFilterBar {...filters} toolbarSlotRef={keepToolbarSlot} />
                  </div>
                  <ViewMenu
                    tabs={tabs}
                    activeView={activeView}
                    hiddenCatalog={hiddenCatalog}
                    isLocked={isLocked}
                    actions={viewActions}
                    loading={viewsLoading}
                    customFieldDefs={customFieldDefs}
                    className="flex-shrink-0"
                  />
                </div>

                {!activeView ? (
                  <TaskListSkeleton rows={4} />
                ) : sectioned ? (
                  <div className="space-y-5">
                    {/* The main list (with the tasks of deleted lists, tasksOfList). With
                        other lists it has the project's name and hides while empty. */}
                    {(() => {
                      const hasLists = projectSections.length > 0;
                      const mainTasks = shownOfList(null);
                      const shown = !hasLists || (filters.hasActiveFilters ? mainTasks.length > 0 : tasksOfList(null).length > 0);
                      if (!shown) return null;
                      return (
                        <ProjectListBlock
                          {...listProps}
                          name={hasLists ? selectedProject.name : null}
                          tasks={mainTasks}
                          tableId={`view-${activeViewId}-main`}
                          toolbarTarget={toolbarSlot}
                          collapsed={hasLists && collapsedLists.has('main')}
                          onToggle={() => toggleList('main')}
                          dropProps={hasLists ? makeSectionDropProps(null) : undefined}
                          dropActive={hasLists && dragOverSectionId === 'main'}
                        />
                      );
                    })()}

                    {/* The other lists. A list's table keeps the columns and sorting it
                        had under its own first view. */}
                    {projectSections.map(section => {
                      const listTasks = shownOfList(section.id);
                      if (filters.hasActiveFilters && listTasks.length === 0) return null;
                      const editing = renamingSectionId === section.id && renamingLocation === 'section';
                      return (
                        <ProjectListBlock
                          key={section.id}
                          {...listProps}
                          listId={section.id}
                          name={section.name}
                          tasks={listTasks}
                          tableId={`view-${section.id}-table-section-${section.id}`}
                          collapsed={collapsedLists.has(section.id)}
                          onToggle={() => toggleList(section.id)}
                          renaming={editing ? {
                            value: renameSectionValue,
                            onChange: setRenameSectionValue,
                            onCommit: () => handleRenameSection(section.id, renameSectionValue),
                            onCancel: stopRenamingList,
                          } : null}
                          onRename={() => { setRenamingSectionId(section.id); setRenamingLocation('section'); setRenameSectionValue(section.name); }}
                          onOpen={() => setSearchParams({ project: selectedProjectId, section: section.id })}
                          dropProps={makeSectionDropProps(section.id)}
                          dropActive={dragOverSectionId === section.id}
                        />
                      );
                    })}

                    <button
                      type="button"
                      onClick={handleCreateSection}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-md text-[12px] text-muted-foreground hover:text-foreground hover:bg-accent transition-colors w-full text-left"
                    >
                      <Plus className="w-3.5 h-3.5" /> {t('projects.add_list')}
                    </button>
                  </div>
                ) : viewMode === 'table' ? (
                  <BulkSelectableTable
                    key={`${activeViewId}-section-${activeSection}`}
                    tasks={shownTasks}
                    projects={projects}
                    onUpdate={handleTaskUpdate}
                    onBulkUpdate={handleTaskUpdate}
                    onOptimisticUpdate={handleOptimisticUpdate}
                    showProjectColumn={false}
                    showSearch={false}
                    showFilters={false}
                    showGrouping
                    showAddButton={false}
                    showCompletedToggle
                    emptyInlineAdd
                    defaultGroupBy="none"
                    defaultSortColumn="manual"
                    prefillProjectId={selectedProjectId}
                    prefillSectionId={activeSection}
                    tableId={`view-${activeViewId}-section-${activeSection}`}
                    projectId={selectedProjectId}
                    teamId={selectedProject.team_id || null}
                    toolbarTarget={toolbarSlot}
                  />
                ) : viewMode === 'list' ? (
                  <ListView {...viewProps} />
                ) : viewMode === 'board' ? (
                  <KanbanBoard {...viewProps} />
                ) : viewMode === 'calendar' ? (
                  <ProjectCalendar {...viewProps} />
                ) : viewMode === 'timeline' ? (
                  <TimelineView {...viewProps} />
                ) : viewMode === 'gallery' ? (
                  <GalleryView {...viewProps} />
                ) : null}
              </>
            )}
          </div>
        )}

        {/* Docs: the project's documents, list or grid (ui_preferences.documents_view_mode,
            per person). Root documents only; a folder shows its own when opened. */}
        {tab === 'docs' && (
          <div className="space-y-3" data-testid="project-docs">
            <div className="flex items-center justify-end">
              <div className="inline-flex items-center rounded-md border border-border bg-transparent overflow-hidden">
                <button
                  type="button"
                  onClick={() => setDocumentsViewMode('list')}
                  title={t('projects.documents_view_list', { defaultValue: 'List view' })}
                  aria-pressed={documentsViewMode === 'list'}
                  className={`h-8 w-8 flex items-center justify-center transition-colors ${
                    documentsViewMode === 'list'
                      ? 'bg-accent text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <ListIcon className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setDocumentsViewMode('grid')}
                  title={t('projects.documents_view_grid', { defaultValue: 'Grid view' })}
                  aria-pressed={documentsViewMode === 'grid'}
                  className={`h-8 w-8 flex items-center justify-center transition-colors ${
                    documentsViewMode === 'grid'
                      ? 'bg-accent text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <LayoutGrid className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <DocumentsBrowser
              docs={rootDocs}
              mode={documentsViewMode}
              onOpen={(id) => navigate(`/app/documents/${id}`)}
              formatDate={formatDate}
              t={t}
            />
          </div>
        )}

        {/* Chats: every chat of the project with its bots; one opens on its own page. */}
        {tab === 'chats' && (
          // The same history as Chats (rows, search, Needs you and bot filters), of this project.
          <div data-testid="project-chats">
            <ChatsHistory project={selectedProject} filters={chatFilters} onFilters={setChatFilters}
              onOpen={(c) => navigate(`/app/chats/${c.id}`)} />
          </div>
        )}

        {/* Context Menu */}
        <ProjectContextMenu
          show={contextMenu.show}
          x={contextMenu.x}
          y={contextMenu.y}
          project={contextMenu.project}
          onClose={closeContextMenu}
          onEdit={() => { handleEditProject(contextMenu.project); closeContextMenu(); }}
          onDelete={() => { handleDeleteProject(contextMenu.project); closeContextMenu(); }}
          onDuplicate={() => { handleDuplicateProject(contextMenu.project); closeContextMenu(); }}
          onToggleFavorite={handleToggleFavorite}
          onAddToTeam={handleAddToTeam}
          onMoveAbove={(p) => { moveProject(p, -1); closeContextMenu(); }}
          onMoveBelow={(p) => { moveProject(p, 1); closeContextMenu(); }}
          onShare={(p) => { setShareModalProject(p); closeContextMenu(); }}
          onActivityLog={(p) => { setActivityLogProject(p); closeContextMenu(); }}
        />

        {/* Add/Edit Project Modal */}
        <AddProjectModal
          isOpen={showAddModal}
          onClose={() => { setShowAddModal(false); setEditingProject(null); }}
          onProjectCreated={handleProjectCreated}
          editingProject={editingProject}
        />

        {/* Add Project to Team Modal */}
        <AddProjectToTeamModal
          show={showAddToTeamModal}
          onClose={() => { setShowAddToTeamModal(false); setAddingToTeamProject(null); }}
          project={addingToTeamProject}
          onSuccess={handleAddToTeamSuccess}
        />

        {/* Share Project Modal */}
        <ShareProjectModal
          isOpen={!!shareModalProject}
          onClose={() => setShareModalProject(null)}
          project={shareModalProject}
        />

        {/* Activity Log */}
        <ActivityLogModal
          project={activityLogProject}
          onClose={() => setActivityLogProject(null)}
        />
      </div>
      {/* The project's chat: next to the work on wide screens, a sheet on narrow ones. */}
      {panel && wideScreen && (
        <div className="sticky top-0 shrink-0 h-[calc(100vh-7rem)]" data-testid="project-chat-aside"
          style={{ width: panelWidth ? `${panelWidth}px` : PANEL_DEFAULT, maxWidth: 'max(400px, calc(100% - 560px))' }}>
          <div role="separator" aria-orientation="vertical" aria-label={t('bots.chat.resize_panel')} title={t('bots.chat.resize_panel')}
            onPointerDown={startPanelResize} onDoubleClick={resetPanelWidth} data-testid="project-chat-resize"
            className="group/resize absolute -left-2.5 top-0 bottom-0 w-2.5 z-10 cursor-col-resize flex items-center justify-center">
            <span className="h-12 w-1 rounded-full bg-border group-hover/resize:bg-brand-icon/60 transition-colors" />
          </div>
          <aside className="h-full rounded-2xl border border-border overflow-hidden">
            {panel}
          </aside>
        </div>
      )}
      <Sheet open={!!panel && !wideScreen} onOpenChange={(open) => { if (!open) setPanel(null); }}>
        <SheetContent side="right" className="p-0 w-full sm:max-w-[560px] flex flex-col [&>button.absolute]:hidden">
          <SheetTitle className="sr-only">{t('bots.chat.chats_title')}</SheetTitle>
          <SheetDescription className="sr-only">{selectedProject.name}</SheetDescription>
          <div className="flex-1 min-h-0" style={{ paddingTop: 'env(safe-area-inset-top)' }}>{!wideScreen && panel}</div>
        </SheetContent>
      </Sheet>
      </div>
    );
  }

  // Projects List View: search first, then one row per project (its color, name, team
  // and open tasks), favorites on top. Sub-projects fold under their parent.
  const filteredIds = new Set(filteredProjects.map(p => p.id));
  const topLevel = filteredProjects.filter(p => !p.parent_id || !filteredIds.has(p.parent_id));
  const childrenByParent = {};
  filteredProjects.forEach(p => {
    if (p.parent_id && filteredIds.has(p.parent_id)) {
      childrenByParent[p.parent_id] = [...(childrenByParent[p.parent_id] || []), p];
    }
  });
  const nested = Object.keys(childrenByParent).length > 0;

  const renderProjectRow = (project, depth = 0) => {
    const stats = getProjectStats(project.id);
    const children = childrenByParent[project.id] || [];
    const isExpanded = expandedProjectIds.has(project.id);
    return (
      <React.Fragment key={project.id}>
        <div
          role="link"
          tabIndex={0}
          onClick={() => handleProjectClick(project)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleProjectClick(project); }}
          onContextMenu={(e) => handleContextMenu(e, project)}
          data-testid="project-row"
          className="group flex items-center gap-2.5 h-12 md:h-10 px-3 cursor-pointer hover:bg-muted dark:hover:bg-white/[0.03] active:bg-accent/60 transition-colors"
          style={depth ? { paddingLeft: 12 + depth * 24 } : undefined}
        >
          {children.length > 0 ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setExpandedProjectIds(prev => {
                  const next = new Set(prev);
                  if (next.has(project.id)) next.delete(project.id);
                  else next.add(project.id);
                  return next;
                });
              }}
              className="-ml-1 p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-accent/50 flex-shrink-0"
              aria-label={isExpanded ? t('ui_pages.projects.collapse_subprojects') : t('ui_pages.projects.expand_subprojects')}
              aria-expanded={isExpanded}
            >
              {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          ) : nested && <span className="-ml-1 w-[18px] flex-shrink-0" />}
          <span className="w-2.5 h-2.5 rounded-[3px] flex-shrink-0" style={{ background: project.color || '#6B7280' }} />
          <span className="flex-1 min-w-0 flex items-baseline gap-2">
            <span className="truncate text-[15px] md:text-sm text-foreground">{project.name}</span>
            {project.team_name && (
              <span className="truncate text-xs text-muted-foreground">{project.team_name}</span>
            )}
          </span>
          <ProjectUnreadDot projectId={project.id} className="flex-shrink-0" />
          {project.is_favorite && (
            <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500 flex-shrink-0" />
          )}
          {stats.pending > 0 && (
            <span className="text-xs text-muted-foreground tabular-nums flex-shrink-0" title={`${stats.pending} ${t('projects.pending')}`}>
              {stats.pending}
            </span>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleContextMenu(e, project); }}
            className="hidden md:inline-flex p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/60 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity flex-shrink-0"
            aria-label={t('ui_pages.projects.project_menu')}
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
          <ChevronRight className="md:hidden w-4 h-4 text-muted-foreground/40 flex-shrink-0" />
        </div>
        {children.length > 0 && isExpanded && children.map(child => renderProjectRow(child, depth + 1))}
      </React.Fragment>
    );
  };

  return (
    <div className="space-y-3 md:space-y-4">
      {/* Title and the one action. On phones the top bar has the title. */}
      <div className="hidden md:flex items-center justify-between gap-3">
        <div className="flex items-baseline gap-2 min-w-0">
          <h1 className="text-lg font-semibold text-foreground">{t('nav.projects')}</h1>
          <span className="text-xs text-muted-foreground tabular-nums">{projects.length}</span>
        </div>
        <Button
          onClick={handleAddProject}
          size="sm"
          className="bg-amber-500 text-black hover:bg-amber-400 h-8 px-3 text-xs"
        >
          <Plus className="w-3.5 h-3.5 mr-1" />
          {t('projects.new_project')}
        </Button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 min-w-0 md:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t('projects.search_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9 md:h-8 rounded-lg text-[13px] bg-transparent border-border focus-visible:border-amber-500/40"
          />
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={handleAddProject}
          aria-label={t('projects.new_project')}
          title={t('projects.new_project')}
          className="md:hidden h-9 w-9 rounded-md flex-shrink-0 border-border"
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>

      {filteredProjects.length === 0 ? (
        <div className="text-center py-16 bg-card border border-border rounded-2xl">
          <Folder className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
          <h3 className="text-lg font-medium text-foreground mb-2">{t('projects.no_projects')}</h3>
          <p className="text-muted-foreground mb-4">
            {searchQuery ? t('projects.try_adjusting_search') : t('projects.no_projects_description')}
          </p>
          {!searchQuery && (
            <Button
              onClick={handleAddProject}
              variant="outline"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('projects.new_project')}
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white dark:bg-white/5 rounded-lg border border-border overflow-hidden divide-y divide-border dark:divide-border/50" data-testid="projects-list">
          {topLevel.map(project => renderProjectRow(project))}
        </div>
      )}

      {/* Context Menu */}
      <ProjectContextMenu
        show={contextMenu.show}
        x={contextMenu.x}
        y={contextMenu.y}
        project={contextMenu.project}
        onClose={closeContextMenu}
        onEdit={() => { handleEditProject(contextMenu.project); closeContextMenu(); }}
        onDelete={() => { handleDeleteProject(contextMenu.project); closeContextMenu(); }}
        onDuplicate={() => { handleDuplicateProject(contextMenu.project); closeContextMenu(); }}
        onToggleFavorite={handleToggleFavorite}
        onAddToTeam={handleAddToTeam}
        onMoveAbove={(p) => { moveProject(p, -1); closeContextMenu(); }}
        onMoveBelow={(p) => { moveProject(p, 1); closeContextMenu(); }}
        onShare={(p) => { setShareModalProject(p); closeContextMenu(); }}
        onActivityLog={(p) => { setActivityLogProject(p); closeContextMenu(); }}
      />

      {/* Add/Edit Project Modal */}
      <AddProjectModal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); setEditingProject(null); }}
        onProjectCreated={handleProjectCreated}
        editingProject={editingProject}
      />

      {/* Add Project to Team Modal */}
      <AddProjectToTeamModal
        show={showAddToTeamModal}
        onClose={() => { setShowAddToTeamModal(false); setAddingToTeamProject(null); }}
        project={addingToTeamProject}
        onSuccess={handleAddToTeamSuccess}
      />

      {/* Share Project Modal */}
      <ShareProjectModal
        isOpen={!!shareModalProject}
        onClose={() => setShareModalProject(null)}
        project={shareModalProject}
      />

      {/* Activity Log */}
      <ActivityLogModal
        project={activityLogProject}
        onClose={() => setActivityLogProject(null)}
      />

    </div>
  );
};

export default ProjectsPage;
